import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Card, CardContent, Typography, Button, Table, TableBody,
  TableCell, TableContainer, TableRow, IconButton,
  CircularProgress, Tooltip, Dialog, DialogTitle, DialogContent,
  DialogActions, TextField, Breadcrumbs, Link, Stack
} from '@mui/material';
import FolderIcon from '@mui/icons-material/Folder';
import InsertDriveFileIcon from '@mui/icons-material/InsertDriveFile';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import CreateNewFolderIcon from '@mui/icons-material/CreateNewFolder';
import NoteAddIcon from '@mui/icons-material/NoteAdd';
import RefreshIcon from '@mui/icons-material/Refresh';
import SaveIcon from '@mui/icons-material/Save';
import CloseIcon from '@mui/icons-material/Close';
import FolderOpenIcon from '@mui/icons-material/FolderOpen';
import { listFiles, readFileContent, saveFileContent, deleteFile } from '../api';

function formatBytes(bytes) {
  if (bytes === 0) return '0 Bytes';
  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
}

const EDITABLE_EXTENSIONS = ['.properties', '.json', '.txt', '.yml', '.yaml', '.log', '.conf', '.list'];

export default function FileManager({ serverName }) {
  const [currentPath, setCurrentPath] = useState('');
  const [files, setFiles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);

  // File Creator States
  const [newFileName, setNewFileName] = useState('');
  const [createFileOpen, setCreateFileOpen] = useState(false);
  const [createIsFolder, setCreateIsIsFolder] = useState(false);

  // Editor States
  const [editorOpen, setEditorOpen] = useState(false);
  const [editPath, setEditPath] = useState('');
  const [editorContent, setEditingContent] = useState('');
  const [editorLoading, setEditorLoading] = useState(false);

  // Deletion State
  const [deleteTarget, setDeleteTarget] = useState(null);

  const fetchFiles = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await listFiles(serverName, currentPath);
      // Sort: folders first, then alphabetically
      const sorted = [...data].sort((a, b) => {
        if (a.isDir && !b.isDir) return -1;
        if (!a.isDir && b.isDir) return 1;
        return a.name.localeCompare(b.name);
      });
      setFiles(sorted);
    } catch (e) {
      console.error("Failed to load files", e);
    } finally {
      setLoading(false);
    }
  }, [serverName, currentPath]);

  useEffect(() => {
    fetchFiles();
  }, [fetchFiles]);

  const handleNavigate = (dirName) => {
    const nextPath = currentPath ? `${currentPath}/${dirName}` : dirName;
    setCurrentPath(nextPath);
  };

  const handleGoBack = () => {
    if (!currentPath) return;
    const parts = currentPath.split('/');
    parts.pop();
    setCurrentPath(parts.join('/'));
  };

  const handleBreadcrumbClick = (index) => {
    const parts = currentPath.split('/');
    const nextPath = parts.slice(0, index + 1).join('/');
    setCurrentPath(nextPath);
  };

  const handleCreateItem = async (e) => {
    e.preventDefault();
    if (!newFileName.trim()) return;
    setActionLoading(true);
    const itemPath = currentPath ? `${currentPath}/${newFileName.trim()}` : newFileName.trim();

    try {
      if (createIsFolder) {
        // Create an empty folder by creating a placeholder file inside
        await saveFileContent(serverName, `${itemPath}/.placeholder`, '');
      } else {
        // Create an empty text file
        await saveFileContent(serverName, itemPath, '');
      }
      setNewFileName('');
      setCreateFileOpen(false);
      fetchFiles();
    } catch (e) {
      alert(`Creation failed: ${e.message}`);
    } finally {
      setActionLoading(false);
    }
  };

  const handleOpenFile = async (fileName) => {
    const filePath = currentPath ? `${currentPath}/${fileName}` : fileName;
    setEditPath(filePath);
    setEditorOpen(true);
    setEditorLoading(true);
    try {
      const { data } = await readFileContent(serverName, filePath);
      setEditingContent(data.content);
    } catch (e) {
      alert(`Failed to load file content: ${e.message}`);
      setEditorOpen(false);
    } finally {
      setEditorLoading(false);
    }
  };

  const handleSaveFile = async () => {
    setEditorLoading(true);
    try {
      await saveFileContent(serverName, editPath, editorContent);
      alert("File saved successfully!");
      setEditorOpen(false);
      fetchFiles();
    } catch (e) {
      alert(`Save failed: ${e.message}`);
    } finally {
      setEditorLoading(false);
    }
  };

  const handleDeleteItem = async () => {
    if (!deleteTarget) return;
    setActionLoading(true);
    const itemPath = currentPath ? `${currentPath}/${deleteTarget}` : deleteTarget;
    try {
      await deleteFile(serverName, itemPath);
      setDeleteTarget(null);
      fetchFiles();
    } catch (e) {
      alert(`Delete failed: ${e.message}`);
    } finally {
      setActionLoading(false);
    }
  };

  const isEditable = (name) => {
    const lower = name.toLowerCase();
    return EDITABLE_EXTENSIONS.some(ext => lower.endsWith(extensionsMapping(ext)));
  };

  const extensionsMapping = (ext) => ext;

  return (
    <Card sx={{
      background: 'rgba(30, 30, 40, 0.4)',
      backdropFilter: 'blur(16px)',
      border: '1px solid rgba(255, 255, 255, 0.08)',
      boxShadow: '0 8px 32px 0 rgba(0,0,0,0.3)',
      borderRadius: 4,
      overflow: 'hidden'
    }}>
      {/* File Manager Header */}
      <Box sx={{
        p: 2.5,
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        borderBottom: '1px solid rgba(255, 255, 255, 0.08)',
        background: 'linear-gradient(90deg, rgba(0,176,255,0.05) 0%, rgba(0,0,0,0) 100%)'
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <FolderOpenIcon sx={{ color: '#00B0FF', filter: 'drop-shadow(0 0 8px rgba(0,176,255,0.4))' }} />
          <Typography variant="h6" sx={{ fontWeight: '800', fontFamily: 'Outfit, sans-serif', letterSpacing: '0.5px' }}>
            File Manager
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <IconButton size="small" onClick={fetchFiles} disabled={loading}>
            <RefreshIcon fontSize="small" />
          </IconButton>
          <Button
            variant="outlined"
            size="small"
            onClick={() => { setCreateIsIsFolder(true); setCreateFileOpen(true); }}
            startIcon={<CreateNewFolderIcon />}
            sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 'bold' }}
          >
            New Folder
          </Button>
          <Button
            variant="outlined"
            size="small"
            color="primary"
            onClick={() => { setCreateIsIsFolder(false); setCreateFileOpen(true); }}
            startIcon={<NoteAddIcon />}
            sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 'bold' }}
          >
            New File
          </Button>
        </Box>
      </Box>

      {/* Path Breadcrumbs Bar */}
      <Box sx={{ p: 2, bgcolor: 'rgba(0,0,0,0.2)', borderBottom: '1px solid rgba(255,255,255,0.05)', display: 'flex', alignItems: 'center', gap: 1 }}>
        {currentPath && (
          <IconButton size="small" onClick={handleGoBack} sx={{ mr: 1 }}>
            <ArrowBackIcon fontSize="small" />
          </IconButton>
        )}
        <Breadcrumbs separator="/" sx={{ color: 'text.secondary', fontSize: '0.85rem', fontFamily: 'monospace' }}>
          <Link
            underline="hover"
            component="span"
            onClick={() => setCurrentPath('')}
            sx={{ cursor: 'pointer', color: !currentPath ? 'primary.main' : 'inherit', fontWeight: !currentPath ? 'bold' : 'normal' }}
          >
            root
          </Link>
          {currentPath && currentPath.split('/').map((part, index) => {
            const isLast = index === currentPath.split('/').length - 1;
            return (
              <Link
                key={index}
                underline="hover"
                component="span"
                onClick={() => handleBreadcrumbClick(index)}
                sx={{
                  cursor: 'pointer',
                  color: isLast ? 'primary.main' : 'inherit',
                  fontWeight: isLast ? 'bold' : 'normal'
                }}
              >
                {part}
              </Link>
            );
          })}
        </Breadcrumbs>
      </Box>

      {/* Directory Table */}
      <CardContent sx={{ p: 0, '&:last-child': { pb: 0 } }}>
        {loading && files.length === 0 ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 6 }}>
            <CircularProgress color="primary" />
          </Box>
        ) : files.length === 0 ? (
          <Box sx={{ p: 6, textAlign: 'center', opacity: 0.6 }}>
            <Typography variant="body2" sx={{ fontStyle: 'italic' }}>
              This folder is empty
            </Typography>
          </Box>
        ) : (
          <TableContainer sx={{ maxHeight: 500 }}>
            <Table stickyHeader size="small">
              <TableBody>
                {files.map((file) => {
                  const isDir = file.isDir;
                  const canEdit = !isDir && isEditable(file.name);
                  return (
                    <TableRow
                      key={file.name}
                      sx={{
                        '&:hover': { bgcolor: 'rgba(255, 255, 255, 0.02)' },
                        cursor: isDir ? 'pointer' : 'default'
                      }}
                      onClick={isDir ? () => handleNavigate(file.name) : undefined}
                    >
                      <TableCell sx={{ borderBottom: '1px solid rgba(255,255,255,0.05)', py: 1.2, pl: 3, width: '60%' }}>
                        <Stack direction="row" alignItems="center" spacing={1.5}>
                          {isDir ? (
                            <FolderIcon sx={{ color: '#00B0FF' }} />
                          ) : (
                            <InsertDriveFileIcon sx={{ color: 'text.secondary' }} />
                          )}
                          <Typography variant="body2" sx={{
                            fontFamily: 'Fira Code, Consolas, monospace',
                            fontWeight: isDir ? '600' : 'normal',
                            color: isDir ? '#00B0FF' : 'text.primary',
                          }}>
                            {file.name}
                          </Typography>
                        </Stack>
                      </TableCell>
                      <TableCell sx={{ borderBottom: '1px solid rgba(255,255,255,0.05)', width: '20%', fontSize: '0.8rem', color: 'text.secondary' }}>
                        {isDir ? 'Folder' : formatBytes(file.size)}
                      </TableCell>
                      <TableCell align="right" sx={{ borderBottom: '1px solid rgba(255,255,255,0.05)', pr: 3, width: '20%' }} onClick={(e) => e.stopPropagation()}>
                        <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 0.5 }}>
                          {canEdit && (
                            <Tooltip title="Edit text config">
                              <IconButton size="small" color="primary" onClick={() => handleOpenFile(file.name)}>
                                <EditIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          )}
                          <Tooltip title="Delete file">
                            <IconButton size="small" color="error" onClick={() => setDeleteTarget(file.name)}>
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </Box>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </CardContent>

      {/* Create Item Dialog */}
      <Dialog open={createFileOpen} onClose={() => setCreateFileOpen(false)} maxWidth="xs" fullWidth>
        <Box component="form" onSubmit={handleCreateItem}>
          <DialogTitle sx={{ fontFamily: 'Outfit, sans-serif', fontWeight: 'bold' }}>
            {createIsFolder ? 'Create Folder' : 'Create New Text File'}
          </DialogTitle>
          <DialogContent>
            <TextField
              autoFocus
              margin="dense"
              label="Name"
              fullWidth
              variant="outlined"
              size="small"
              value={newFileName}
              onChange={(e) => setNewFileName(e.target.value)}
              placeholder={createIsFolder ? 'e.g. plugins' : 'e.g. motd.txt'}
              sx={{ mt: 1 }}
            />
          </DialogContent>
          <DialogActions sx={{ p: 2.5, pt: 1 }}>
            <Button onClick={() => setCreateFileOpen(false)} variant="outlined" size="small" sx={{ textTransform: 'none' }}>
              Cancel
            </Button>
            <Button type="submit" disabled={actionLoading || !newFileName.trim()} variant="contained" color="primary" size="small" sx={{ textTransform: 'none' }}>
              Create
            </Button>
          </DialogActions>
        </Box>
      </Dialog>

      {/* Editor Dialog */}
      <Dialog open={editorOpen} onClose={() => !editorLoading && setEditorOpen(false)} fullWidth maxWidth="md">
        <DialogTitle sx={{
          bgcolor: '#14141d',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          borderBottom: '1px solid rgba(255,255,255,0.08)'
        }}>
          <Typography variant="h6" sx={{ fontFamily: 'Outfit, sans-serif', fontWeight: 'bold', overflow: 'hidden', textOverflow: 'ellipsis', pr: 2 }}>
            📄 Edit Config: {editPath.split('/').pop()}
          </Typography>
          <IconButton size="small" onClick={() => setEditorOpen(false)} disabled={editorLoading}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ bgcolor: '#09090e', p: 0 }}>
          {editorLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 10 }}>
              <CircularProgress />
            </Box>
          ) : (
            <textarea
              value={editorContent}
              onChange={(e) => setEditingContent(e.target.value)}
              style={{
                width: '100%',
                height: '450px',
                backgroundColor: '#0a0a0f',
                color: '#eceef5',
                border: 'none',
                padding: '20px',
                fontFamily: 'Fira Code, Consolas, monospace',
                fontSize: '0.85rem',
                lineHeight: '1.5',
                outline: 'none',
                resize: 'none',
                boxSizing: 'border-box'
              }}
            />
          )}
        </DialogContent>
        <DialogActions sx={{ p: 2.5, bgcolor: '#14141d', borderTop: '1px solid rgba(255,255,255,0.08)' }}>
          <Button onClick={() => setEditorOpen(false)} disabled={editorLoading} variant="outlined" sx={{ textTransform: 'none' }}>
            Cancel
          </Button>
          <Button onClick={handleSaveFile} disabled={editorLoading} variant="contained" color="success" startIcon={<SaveIcon />} sx={{ textTransform: 'none' }}>
            Save File
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={!!deleteTarget} onClose={() => setDeleteTarget(null)}>
        <DialogTitle sx={{ fontFamily: 'Outfit, sans-serif', fontWeight: 'bold' }}>Confirm Deletion</DialogTitle>
        <DialogContent sx={{ minWidth: 280 }}>
          Are you sure you want to delete <strong>{deleteTarget}</strong>?
          <Typography variant="caption" display="block" color="error" sx={{ mt: 1.5 }}>
            ⚠️ Danger: This will recursively delete folders and all nested files forever!
          </Typography>
        </DialogContent>
        <DialogActions sx={{ p: 2, pt: 1 }}>
          <Button onClick={() => setDeleteTarget(null)} variant="outlined" size="small" sx={{ textTransform: 'none' }}>
            Cancel
          </Button>
          <Button onClick={handleDeleteItem} variant="contained" color="error" size="small" sx={{ textTransform: 'none' }}>
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Card>
  );
}
